import {LAYER_GROUPS as GROUPS,LAYER_LABELS as CAT_LABELS,LAYER_DESCRIPTIONS,CURATED_CATEGORIES,ALL_CATEGORIES} from '../utils/layers.js';
// Layers pane, category filter chips + visibility toggle + time transport.
// Reuses DEFAULT_CATEGORIES from existing FilterTray. Categories are plain
// strings; we group them locally for visual organization. Active = chip
// glows accent, inactive = dimmed. The time transport (play/pause + speed +
// ±72h) shares the desktop TimeScrubber engine via useTimePlayback.

import { useCallback } from 'react';
import { useTimePlayback, fmtTimeOffset, TIME_MAX_S } from '../hooks/useTimePlayback.js';

const MONO = 'var(--mono)';

// Groups mirror FilterTray.jsx GROUPS structure for visual consistency.
const S = {
  section: { marginBottom: 16 },
  label: {
    color: 'var(--txt-dim)', fontSize: 'var(--ui-label)', letterSpacing: '0.18em',
    fontFamily: MONO, fontWeight: 700, textTransform: 'uppercase',
    marginBottom: 8, marginTop: 4,
  },
  groupLabel: {
    color: 'var(--txt-mute)', fontSize: 'var(--ui-small)', letterSpacing: '0.12em',
    fontFamily: MONO, fontWeight: 700,
    marginBottom: 5, marginTop: 8, textTransform: 'uppercase',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: (active) => ({
    padding: '8px 12px', minHeight: 44,
    background: active ? 'var(--amber-2)' : 'var(--bg-2)',
    border: `1px solid ${active ? 'var(--accent)' : 'var(--hair-2)'}`,
    borderRadius: 4,
    color: active ? 'var(--accent)' : 'var(--txt-mute)',
    fontFamily: MONO, fontSize: 'var(--ui-label)', fontWeight: 700,
    letterSpacing: '0.05em',
    cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center',
  }),
  toggleRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 12px',
    background: 'var(--bg-2)',
    border: '1px solid var(--hair)', borderRadius: 4,
    marginBottom: 4, cursor: 'pointer', minHeight: 44,
  },
  toggleLabel: { color: 'var(--txt)', fontFamily: MONO, fontSize: 'var(--ui-body)' },
  toggleSwitch: (on) => ({
    width: 36, height: 22, borderRadius: 11,
    background: on ? 'var(--accent)' : 'var(--hair-2)',
    position: 'relative', cursor: 'pointer',
    transition: 'background 200ms',
    flexShrink: 0,
  }),
  toggleKnob: (on) => ({
    position: 'absolute', top: 2,
    left: on ? 16 : 2,
    width: 18, height: 18, borderRadius: '50%',
    background: 'var(--bg)',
    transition: 'left 200ms',
  }),
  sliderLabel: {
    display: 'flex', justifyContent: 'space-between',
    fontFamily: MONO, fontSize: 'var(--ui-label)', color: 'var(--txt-dim)',
    marginBottom: 4,
  },
  sliderVal: { color: 'var(--amber)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  transportBtn: (on) => ({
    width: 34, height: 30, borderRadius: 4, cursor: 'pointer', padding: 0,
    fontFamily: MONO, fontSize: 'var(--ui-body)',
    background: on ? 'var(--bg-2)' : 'transparent',
    border: `1px solid ${on ? 'var(--cyan)' : 'var(--hair-2)'}`,
    color: on ? 'var(--cyan)' : 'var(--txt-dim)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  speedPill: (on) => ({
    minWidth: 36, height: 28, borderRadius: 4, cursor: 'pointer',
    fontFamily: MONO, fontSize: 'var(--ui-label)', fontWeight: 700, letterSpacing: '0.04em',
    background: on ? 'var(--bg-2)' : 'transparent',
    border: `1px solid ${on ? 'var(--cyan)' : 'var(--hair-2)'}`,
    color: on ? 'var(--cyan)' : 'var(--txt-dim)', flexShrink: 0,
  }),
};

export default function MobilePaneLayers({ filters, onChange }) {
  const cats = filters.categories || [];
  const offsetSec = filters.time_offset_sec || 0;
  // onChange is App's setFilters, supports a functional update, so the scrub
  // callback stays stable and the playback interval doesn't restart every tick.
  const scrub = useCallback((sec) => onChange(f => ({ ...f, time_offset_sec: sec })), [onChange]);
  const { isPlaying, setIsPlaying, speed, setSpeed } = useTimePlayback(offsetSec, scrub);
  const toggleCat = (id) => {
    const next = cats.includes(id) ? cats.filter(x => x !== id) : [...cats, id];
    onChange({ ...filters, categories: next });
  };

  return (
    <div>
      <div style={S.section}>
        <div style={S.label}>Horizon and sunlight</div><p className="og-layer-help">Above the horizon does not guarantee an observable object. Check the pass rating, darkness, clouds and local sightline.</p>
        <button type="button" aria-label="Above horizon only" aria-pressed={!!filters.visible_only} style={{...S.toggleRow,width:'100%',textAlign:'left',borderColor:'var(--hair-2)'}}
             onClick={() => onChange({ ...filters, visible_only: !filters.visible_only })}>
          <span style={S.toggleLabel}>Above horizon only <small style={{fontSize:'var(--ui-small)'}}>· {filters.visible_only?'On':'Off'}</small></span>
          <div aria-hidden="true" style={S.toggleSwitch(filters.visible_only)}>
            <div style={S.toggleKnob(filters.visible_only)} />
          </div>
        </button>
        <button type="button" aria-label="Sunlit objects only" aria-pressed={!!filters.sunlit_only} style={{...S.toggleRow,width:'100%',textAlign:'left',borderColor:'var(--hair-2)'}}
             onClick={() => onChange({ ...filters, sunlit_only: !filters.sunlit_only })}>
          <span style={S.toggleLabel}>Sunlit objects only <small style={{fontSize:'var(--ui-small)'}}>· {filters.sunlit_only?'On':'Off'}</small></span>
          <div aria-hidden="true" style={S.toggleSwitch(filters.sunlit_only)}>
            <div style={S.toggleKnob(filters.sunlit_only)} />
          </div>
        </button>
      </div>

      <div style={S.section}>
        <div style={S.label}>Layers · {cats.length} active</div>
        <p className="og-layer-help">Curated shows stations, science missions, alerts and launches. Add any layer below; color is not a visibility rating.</p>
        <button style={S.chip(false)} onClick={()=>onChange({...filters,categories:CURATED_CATEGORIES})}>Curated layers</button>
        {/* Bulk toggles, parity with LayerMatrix's all-on/off (R1: layers.all-on-off) */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          <button
            onClick={() => onChange({ ...filters, categories: ALL_CATEGORIES })}
            style={{ ...S.chip(false), flex: 1, justifyContent: 'center', color: 'var(--accent)' }}
          >ALL ON</button>
          <button
            onClick={() => onChange({ ...filters, categories: [] })}
            style={{ ...S.chip(false), flex: 1, justifyContent: 'center' }}
          >ALL OFF</button>
        </div>
        {GROUPS.map(g => (
          <div key={g.label}>
            <div style={S.groupLabel}>{g.label}</div>
            <div style={S.chips}>
              {g.cats.map(c => (
                <div key={c} className="og-layer-choice"><button key={c} onClick={() => toggleCat(c)} aria-pressed={cats.includes(c)} style={S.chip(cats.includes(c))}>
                  {CAT_LABELS[c] || c}
                </button><p>{LAYER_DESCRIPTIONS[c]}</p></div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={S.section}>
        <div style={S.label}>Time</div>

        {/* transport, play/pause + speed, mirrors desktop TimeScrubber */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
          <button
            onClick={() => setIsPlaying(p => !p)}
            aria-pressed={isPlaying}
            title={isPlaying ? 'Pause playback' : 'Play time forward (stops at +72h)'}
            style={S.transportBtn(isPlaying)}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
          {[1, 60, 600].map(s => (
            <button key={s} onClick={() => setSpeed(s)}
                    title={`${s}× real-time`} style={S.speedPill(speed === s)}>
              {s}{'×'}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 'var(--ui-body)', fontWeight: 700,
                         fontVariantNumeric: 'tabular-nums',
                         color: offsetSec === 0 ? 'var(--green)' : 'var(--amber)' }}>
            {offsetSec === 0 ? '● LIVE' : fmtTimeOffset(offsetSec)}
          </span>
        </div>

        <div style={S.sliderLabel}>
          <span>-72h</span>
          <span>+72h</span>
        </div>
        <input
          type="range"
          min={-TIME_MAX_S} max={TIME_MAX_S} step={60}
          value={offsetSec}
          onChange={(e) => { setIsPlaying(false); scrub(parseInt(e.target.value)); }}
          style={{ width: '100%', accentColor: 'var(--amber)' }}
        />
        {offsetSec !== 0 && (
          <button
            onClick={() => { setIsPlaying(false); scrub(0); }}
            style={{
              marginTop: 8, padding: '6px 12px',
              background: 'var(--bg-2)', border: '1px solid var(--hair-2)',
              borderRadius: 3, color: 'var(--accent)',
              fontFamily: MONO, fontSize: 'var(--ui-label)', fontWeight: 700, letterSpacing: '0.08em',
              cursor: 'pointer',
            }}
          >RESET TO NOW</button>
        )}
      </div>
    </div>
  );
}
